import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  padding: 2rem;
  background: ${theme.colors.background.primary};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Button = styled.button`
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: ${theme.colors.primary.dark};
  }
`;

const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const Widget = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
`;

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const WidgetTitle = styled.h3`
  color: ${theme.colors.text.primary};
  margin: 0;
  font-size: 1.1rem;
`;

const WidgetValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${theme.colors.primary.main};
  margin-bottom: 0.5rem;
`;

const WidgetPath = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  font-family: monospace;
`;

const AddForm = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
  font-size: 1rem;
`;

const WorkingDashboard = () => {
  const [widgets, setWidgets] = useState([]);
  const [signalKData, setSignalKData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWidget, setNewWidget] = useState({
    name: '',
    dataPath: ''
  });

  useEffect(() => {
    loadSignalKData();
    const interval = setInterval(loadSignalKData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadSignalKData = async () => {
    try {
      const response = await api.get('/api/signalk/data');
      setSignalKData(response.data);
    } catch (error) {
      console.error('Error loading SignalK data:', error);
    }
  };

  const getSignalKValue = (dataPath) => {
    if (!dataPath || !signalKData) return '--';
    
    try {
      const pathParts = dataPath.split('.');
      let value = signalKData;
      
      for (const part of pathParts) {
        if (value && typeof value === 'object' && part in value) {
          value = value[part];
        } else {
          return '--';
        }
      }
      
      if (value && typeof value === 'object' && 'value' in value) {
        const rawValue = value.value;
        if (typeof rawValue === 'number') {
          if (dataPath.includes('voltage')) {
            return rawValue.toFixed(2) + 'V';
          } else if (dataPath.includes('current')) {
            return rawValue.toFixed(2) + 'A';
          } else if (dataPath.includes('temperature')) {
            return rawValue.toFixed(1) + '°C';
          }
          return rawValue.toFixed(2);
        }
        return rawValue;
      }
      
      return '--';
    } catch (error) {
      return '--';
    }
  };

  const addWidget = () => {
    if (!newWidget.name || !newWidget.dataPath) {
      toast.error('Please enter both name and data path');
      return;
    }

    const widget = {
      id: Date.now(),
      name: newWidget.name,
      dataPath: newWidget.dataPath
    };

    setWidgets(prev => [...prev, widget]);
    setNewWidget({ name: '', dataPath: '' });
    setShowAddForm(false);
    toast.success('Widget added successfully!');
  };

  const removeWidget = (id) => {
    setWidgets(prev => prev.filter(w => w.id !== id));
    toast.success('Widget removed');
  };

  return (
    <Container>
      <Header>
        <Title>Working Dashboard</Title>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'Cancel' : 'Add Widget'}
        </Button>
      </Header>

      {showAddForm && (
        <AddForm>
          <h3>Add New Widget</h3>
          <Input
            type="text"
            placeholder="Widget Name (e.g., House Battery Voltage)"
            value={newWidget.name}
            onChange={(e) => setNewWidget(prev => ({ ...prev, name: e.target.value }))}
          />
          <Input
            type="text"
            placeholder="Data Path (e.g., electrical.batteries.239.voltage)"
            value={newWidget.dataPath}
            onChange={(e) => setNewWidget(prev => ({ ...prev, dataPath: e.target.value }))}
          />
          <Button onClick={addWidget}>Add Widget</Button>
        </AddForm>
      )}

      <WidgetGrid>
        {widgets.map(widget => (
          <Widget key={widget.id}>
            <WidgetHeader>
              <WidgetTitle>{widget.name}</WidgetTitle>
              <Button 
                onClick={() => removeWidget(widget.id)}
                style={{ 
                  background: theme.colors.error.main,
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem'
                }}
              >
                Remove
              </Button>
            </WidgetHeader>
            <WidgetValue>
              {getSignalKValue(widget.dataPath)}
            </WidgetValue>
            <WidgetPath>{widget.dataPath}</WidgetPath>
          </Widget>
        ))}
        
        {widgets.length === 0 && !showAddForm && (
          <div style={{ 
            textAlign: 'center', 
            color: theme.colors.text.secondary,
            gridColumn: '1 / -1',
            padding: '2rem'
          }}>
            No widgets yet. Click "Add Widget" to create your first widget.
          </div>
        )}
      </WidgetGrid>
    </Container>
  );
};

export default WorkingDashboard;